import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import "./App.css";
import Landing from "@/pages/Landing";
import AlphabotEntry from "@/components/AlphabotEntry";
import KairosisHome from "@/pages/KairosisHome";
import RoarLanding from "@/pages/RoarLanding";
import LegalPage from "@/pages/LegalPage";
import RoarDashboard from "@/pages/RoarDashboard";
import MobileBottomNav from "@/components/MobileBottomNav";
import LivePnL from "@/pages/LivePnL";
import Performance from "@/pages/Performance";
import Leaderboard from "@/pages/Leaderboard";
import Pricing from "@/pages/Pricing";
import PropFirm from "@/pages/PropFirm";
import HowItWorks from "@/pages/HowItWorks";
import FAQ from "@/pages/FAQ";
import DashboardEnhanced from "@/pages/DashboardEnhanced";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import CryptoPayment from "@/pages/CryptoPayment";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentPending from "@/pages/PaymentPending";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";
import { isPaidPlan, normalizePlan } from "@/lib/plans";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionRoute = ({ children }) => {
  const [state, setState] = useState("loading");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setState("no_token");
      return;
    }

    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const plan = normalizePlan(res.data.subscription_plan);
        const status = res.data.subscription_status || "inactive";
        if (isPaidPlan(plan, status)) {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          user.subscription_plan = plan;
          user.subscription_status = status;
          localStorage.setItem("user", JSON.stringify(user));
          setState("allowed");
        } else {
          setState("no_sub");
        }
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setState("no_token");
      });
  }, []);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (state === "no_token") return <Navigate to="/pricing" replace />;
  if (state === "no_sub") return <Navigate to="/pricing" replace />;
  return children;
};

const RoarRoute = ({ children }) => {
  const [state, setState] = useState("loading");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setState("no_access");
      return;
    }

    axios
      .get(`${API}/roar/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data.roar_access) {
          setState("allowed");
        } else {
          setState("no_access");
        }
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setState("no_access");
      });
  }, []);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6D00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (state === "no_access") return <Navigate to="/" replace state={{ accessDenied: true }} />;
  return children;
};

const WhatsAppButton = () => (
  <a
    href="https://wa.link/og6izz"
    target="_blank"
    rel="noopener noreferrer"
    title="Chat with us on WhatsApp"
    className="!bottom-[92px] md:!bottom-[24px]"
    style={{
      position: "fixed",
      bottom: "24px",
      right: "24px",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      backgroundColor: "#25D366",
      boxShadow: "0 4px 20px rgba(37,211,102,0.45)",
      cursor: "pointer",
      animation: "wa-pulse 2.5s infinite",
      textDecoration: "none",
    }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="30" height="30" fill="white">
      <path d="M16 2C8.268 2 2 8.268 2 16c0 2.47.643 4.788 1.77 6.8L2 30l7.38-1.74A13.93 13.93 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.5a11.44 11.44 0 0 1-5.84-1.6l-.42-.25-4.38 1.03 1.06-4.27-.27-.44A11.5 11.5 0 1 1 16 27.5zm6.29-8.63c-.34-.17-2.02-1-2.34-1.11-.32-.11-.55-.17-.78.17-.23.34-.9 1.11-1.1 1.34-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.74-1.69-1.01-.9-1.69-2.02-1.89-2.36-.2-.34-.02-.53.15-.7.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6-.09-.17-.78-1.88-1.07-2.58-.28-.68-.57-.59-.78-.6h-.67c-.23 0-.6.09-.91.43-.31.34-1.2 1.17-1.2 2.85s1.22 3.3 1.4 3.53c.17.23 2.42 3.7 5.86 5.19.82.35 1.46.56 1.95.72.82.26 1.57.22 2.16.13.66-.1 2.02-.82 2.31-1.62.29-.8.29-1.48.2-1.62-.09-.14-.31-.23-.65-.4z" />
    </svg>
    <style>{`
      @keyframes wa-pulse {
        0%   { box-shadow: 0 0 0 0 rgba(37,211,102,0.5); }
        70%  { box-shadow: 0 0 0 12px rgba(37,211,102,0); }
        100% { box-shadow: 0 0 0 0 rgba(37,211,102,0); }
      }
    `}</style>
  </a>
);

function App() {
  const path = window.location.pathname;

  if (path === "/" || path === "") {
    return <KairosisHome />;
  }

  // Trubon is a single marketing page inside Kairosis; the app itself is a
  // separate deployment, so every CTA on it points outward rather than routing
  // here. No BrowserRouter needed for one page.
  // Company + risk page, shared by every product on the site.
  if (path.startsWith("/legal")) {
    return (
      <div className="App">
        <LegalPage />
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  if (path.startsWith("/roar")) {
    return (
      <div className="App">
        <BrowserRouter basename="/roar">
          <Routes>
            <Route path="/" element={<RoarLanding />} />
            <Route
              path="/dashboard"
              element={
                <RoarRoute>
                  <RoarDashboard />
                </RoarRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter basename="/alphabot">
        <Routes>
          <Route path="/" element={<AlphabotEntry />} />
          <Route path="/welcome" element={<Landing />} />
          <Route path="/live" element={<LivePnL />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/prop-firm" element={<PropFirm />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/payment/crypto/:plan" element={<CryptoPayment />} />
          <Route path="/payment/pending" element={<PaymentPending />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route
            path="/dashboard"
            element={
              <SubscriptionRoute>
                <DashboardEnhanced />
              </SubscriptionRoute>
            }
          />
        </Routes>
        <MobileBottomNav />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <WhatsAppButton />
    </div>
  );
}

export default App;
